import React, { useCallback, useEffect, useState } from 'react';
import { ReactComponent as NoProcurement } from 'assets/icons/no-procurement.svg';
import { apiService } from 'services/base.service';
import ActivityIndicator from 'components/base/loader/activityIndicator';
import { debounce, handleApiError } from 'utils/functions';
import useDashboardContext from 'contexts/dashboard.context';
import TransferRequestCard from 'components/transfer-request-card';
import { TransferRequestType } from 'models/transfer-request.model';
import { ENUM_REQUEST_STATUS } from 'models/common.model';
import RequestListFilters from 'components/base/request-list-filters';
import RequestListLayout from 'components/base/request-list-layout';
import { TransferStoreTypeParam } from '../store-type';
export type DateRangeType = {
  startDate: Date;
  endDate: Date;
};

type RequestStatusFilter = ENUM_REQUEST_STATUS;

type TransferFilters = {
  search: string;
  status: RequestStatusFilter[];
  dateRange: DateRangeType | null;
  current_page: number;
};

const List = ({
  storeType,
  direction
}: {
  storeType: TransferStoreTypeParam;
  direction: 'to' | 'from';
}) => {
  const [isFetchingProcurements, setIsFetchingProcurements] = useState(false);
  const [transfers, setTransfers] = useState<TransferRequestType[]>([]);
  const [filters, setFilters] = useState<TransferFilters>({
    search: '',
    status: [],
    dateRange: null,
    current_page: 1
  });
  const { activeStore } = useDashboardContext();

  const normalizeStatusFilter = (values: RequestStatusFilter[]) => {
    if (values.length === 0) {
      return [];
    }
    return values;
  };

  const fetchProcurements = async (
    searchString: string,
    status: RequestStatusFilter[],
    current_page?: number,
    startDate?: Date,
    endDate?: Date
  ) => {
    if (!activeStore?.id) return;
    try {
      setIsFetchingProcurements(true);
      const normalizedStatus = normalizeStatusFilter(status);
      const statusParam = normalizedStatus.length === 1 ? normalizedStatus[0] : normalizedStatus;
      const response = await apiService.get(
        `main-store-transfer/${storeType}/store/${activeStore.id}`,
        {
          version: 'v2',
          params: {
            transfer_type: storeType,
            store_id: activeStore.id,
            title: searchString,
            size: 15,
            direction,
            sort: statusParam,
            page: current_page || '1',
            created_at_after: startDate?.toISOString().split('T')[0] || '',
            created_at_before: endDate?.toISOString().split('T')[0] || ''
          }
        }
      );
      if (response) {
        setTransfers(response.data.data);
      }
    } catch (err: any) {
      handleApiError(err);
    } finally {
      setIsFetchingProcurements(false);
    }
  };

  const debounceFetchProcurements = useCallback(debounce(fetchProcurements, 1000), [
    activeStore?.id,
    storeType,
    direction
  ]);

  useEffect(() => {
    setIsFetchingProcurements(true);
    setTransfers([]);
    debounceFetchProcurements(
      filters.search,
      filters.status,
      filters.current_page,
      filters.dateRange?.startDate,
      filters.dateRange?.endDate
    );
  }, [filters, activeStore?.id, storeType, direction]);

  return (
    <RequestListLayout
      showHeader={false}
      filters={
        <RequestListFilters
          statusMode='multi'
          statusOptions={[
            { text: ENUM_REQUEST_STATUS.REJECTED, value: ENUM_REQUEST_STATUS.REJECTED },
            { text: ENUM_REQUEST_STATUS.RECEIVED, value: ENUM_REQUEST_STATUS.RECEIVED },
            { text: ENUM_REQUEST_STATUS.SENT, value: ENUM_REQUEST_STATUS.SENT },
            { text: ENUM_REQUEST_STATUS.REQUESTED, value: ENUM_REQUEST_STATUS.REQUESTED }
          ]}
          filters={filters}
          onFilterChange={setFilters}
          pageCount={1}
        />
      }>
      {isFetchingProcurements ? (
        <div className='pt-10'>
          <ActivityIndicator />
        </div>
      ) : transfers.length === 0 ? (
        <div className='grid place-items-center mt-24'>
          <NoProcurement className='mb-5' />
          <p className='font-medium text-[#5E6164]'>Youï¿½?Tre yet to make any transfer</p>
        </div>
      ) : (
        <div className='mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
          {transfers.map((p, i) => (
            <TransferRequestCard key={i} request={p} />
          ))}
        </div>
      )}
    </RequestListLayout>
  );
};

export default List;
